import { format } from 'date-fns'
import { motion } from 'framer-motion'
import React, { MouseEvent, createContext, useContext, useId, useRef, useState } from 'react'
import { monotoneCubicInterpolation } from './monotoneCubicInterpolation'

const findClosestPoint = (points: ChartPoint[], mouseX: number) => {
  if (points.length === 0) return
  return points.reduce(
    (closest, current) =>
      Math.abs(mouseX - current.x) < Math.abs(mouseX - closest.x) ? current : closest,
    points[0]
  )
}

const findPathPoint = (path: SVGPathElement, x: number) => {
  const pathLength = path.getTotalLength()
  let start = 0
  let end = pathLength
  let target = (start + end) / 2

  // Ensure that x is within the range of the path
  x = Math.max(x, path.getPointAtLength(0).x)
  x = Math.min(x, path.getPointAtLength(pathLength).x)

  while (target >= start && target <= pathLength) {
    const point = path.getPointAtLength(target)

    if (Math.abs(point.x - x) < 1) {
      return point
    } else if (point.x > x) {
      end = target
    } else {
      start = target
    }
    target = (start + end) / 2
  }
}

const IndicatorLabel = ({ x, dateFormat }: { x: number; dateFormat: string }) => {
  const { width, points } = useChart()

  const point = findClosestPoint(points, x)
  if (!point) return null

  const label = format(new Date(point.timestamp), dateFormat)
  const textLabelWidth = (label?.length || 0) * 7 // simplified estimate
  const overflowLeft = x - textLabelWidth / 2 < 0
  const overflowRight = x + textLabelWidth / 2 > width

  let positionX = x - textLabelWidth / 2
  let textAlign = 'center'
  if (overflowLeft) {
    positionX = x
    textAlign = 'right'
  } else if (overflowRight) {
    positionX = x - textLabelWidth
    textAlign = 'left'
  }

  return (
    <text
      y={15}
      width={textLabelWidth}
      className="fill-fg-2 font-text tabular"
      fontWeight={600}
      fontSize="13px"
      x={positionX}
      textAnchor={textAlign}
    >
      {label}
    </text>
  )
}

const Indicator = ({
  position: { x, y },
  dateFormat,
}: {
  position: Position
  dateFormat: string
}) => {
  const { height } = useChart()

  return (
    <>
      <line
        x1={x}
        y1={25}
        x2={x}
        y2={height - 10}
        className="stroke-hairline-strong"
        strokeWidth="2"
        strokeDasharray="5, 5"
      />
      <circle cx={x} cy={y} r="7" className="fill-bg" />
      <circle cx={x} cy={y} r="4" className="fill-accent" />
      <IndicatorLabel x={x} dateFormat={dateFormat} />
    </>
  )
}

const ChartContext = createContext<{
  width: number
  height: number
  points: ChartPoint[]
} | null>(null)

const useChart = () => {
  const c = useContext(ChartContext)
  if (!c) throw new Error('useChart must be used inside a chart')
  return c
}

type ChartData = { timestamp: number; price: number }
type Position = { x: number; y: number }
type ChartPoint = ChartData & Position

export const LineChart = ({
  width,
  height,
  paddingY,
  data,
  dateFormat,
  onMouseMove,
}: {
  width: number
  height: number
  paddingY: number
  data: ChartData[]
  dateFormat: string
  onMouseMove: (pointData?: ChartPoint) => void
}) => {
  const [indicator, setIndicator] = useState<Position | null>(null)
  const pathRef = useRef<SVGPathElement>(null)

  const maxY = Math.max(...data.map((item) => item.price))
  const minY = Math.min(...data.map((item) => item.price))

  const yScale = (height - 2 * paddingY) / (maxY - minY || 1)
  const validData = data.filter((d) => d.price)
  const points = validData.map(({ price, timestamp }, index) => {
    const x = ((index + 1) / validData.length) * width
    const y = height - paddingY - (price - minY) * yScale
    return { price, timestamp, x, y }
  })

  const d = monotoneCubicInterpolation(points)
  // Close the line down to the baseline for the area fill.
  const areaD =
    points.length > 1
      ? `${d} L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`
      : ''
  const gradientId = `line-chart-fade-${useId().replace(/:/g, '')}`

  const handleMouseMove = (event: MouseEvent<SVGSVGElement>) => {
    const svgContainer = event.currentTarget

    const { left } = svgContainer.getBoundingClientRect()
    const mouseX = event.clientX - left

    const path = pathRef.current
    if (!path) return

    onMouseMove(findClosestPoint(points, mouseX))

    const point = findPathPoint(path, mouseX)
    if (point) {
      setIndicator(point)
    }
  }

  const onMouseLeave = () => {
    setIndicator(null)
    onMouseMove(undefined)
  }

  return (
    <ChartContext.Provider value={{ width, height, points }}>
      <div style={{ width, maxWidth: width }}>
        <motion.svg
          viewBox={`0 0 ${width} ${height}`}
          role="presentation"
          onMouseMove={handleMouseMove}
          onMouseLeave={onMouseLeave}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" style={{ stopColor: 'var(--plug-accent)', stopOpacity: 0.2 }} />
              <stop offset="100%" style={{ stopColor: 'var(--plug-accent)', stopOpacity: 0 }} />
            </linearGradient>
          </defs>
          {areaD && (
            <motion.path animate={{ d: areaD }} fill={`url(#${gradientId})`} stroke="none" />
          )}
          <motion.path
            ref={pathRef as any}
            animate={{ d }}
            fill="none"
            className="stroke-accent"
            strokeWidth={3}
            strokeLinecap="round"
          />
          {indicator && <Indicator position={indicator} dateFormat={dateFormat} />}
        </motion.svg>
      </div>
    </ChartContext.Provider>
  )
}
